// # User Edit Dialog Component
import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  DialogContentText,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { db } from '../../../../auth/firebase';
import { writeBatch, doc, getDoc, collection } from 'firebase/firestore';
import { countryCodes } from '../../../../utils/phone_code';
import { nationalityData } from '../../../../utils/nationality_data';
import { 
  MdVerified, 
  MdWarning, 
  MdPerson, 
  MdEmail, 
  MdVpnKey, 
  MdAdminPanelSettings, 
  MdPhone, 
  MdWc, 
  MdPublic, 
  MdSchool, 
  MdTimeline, 
  MdGrade,
  MdAccessTime,
  MdUpdate,
  MdLogin,
  MdDevices,
  MdLanguage,
  MdLocationOn
} from 'react-icons/md';
import './user_edit.css';

const UserEdit = ({ 
  open, 
  onClose, 
  selectedUser, 
  editData, 
  setEditData, 
  onSave,
  isEditing,
  setIsEditing,
  onDelete,
  isDeleting,
  updateError 
}) => {
  const [lastSignIn, setLastSignIn] = useState(null);

  // # Get last sign in data
  useEffect(() => {
    const fetchLastSignIn = async () => {
      if (!selectedUser?.uid) return;

      try {
        // # Get current month key
        const now = new Date();
        const currentMonthKey = `${now.getFullYear()}_${(now.getMonth() + 1).toString().padStart(2, '0')}`;
        
        // # Get activity document
        const activityRef = doc(db, 'activities', selectedUser.uid);
        const activityDoc = await getDoc(activityRef);
        
        if (activityDoc.exists()) {
          const data = activityDoc.data();
          const currentMonth = data[currentMonthKey];
          
          if (currentMonth?.auth_history?.sign_ins) {
            // # Get all sign ins and sort by timestamp
            const signIns = Object.entries(currentMonth.auth_history.sign_ins)
              .map(([id, data]) => ({
                id,
                ...data
              }))
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

            // # Get the most recent sign in
            if (signIns.length > 0) {
              setLastSignIn(signIns[0]);
              return;
            }
          }
          
          // # If no sign ins in current month, check previous month
          const prevDate = new Date(now.getFullYear(), now.getMonth() - 1);
          const prevMonthKey = `${prevDate.getFullYear()}_${(prevDate.getMonth() + 1).toString().padStart(2, '0')}`;
          
          const prevMonth = data[prevMonthKey];
          if (prevMonth?.auth_history?.sign_ins) {
            const signIns = Object.entries(prevMonth.auth_history.sign_ins)
              .map(([id, data]) => ({
                id,
                ...data
              }))
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

            if (signIns.length > 0) {
              setLastSignIn(signIns[0]);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching last sign in:', error);
      }
    };

    fetchLastSignIn();
  }, [selectedUser?.uid]);

  // # Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // # Handle select changes
  const handleSelectChange = (name, value) => {
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // # Format name for display
  const formatName = (user) => {
    if (!user) return 'N/A';
    const parts = [
      user.firstName,
      user.middleName,
      user.lastName
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : (user.displayName || user.email || 'N/A');
  };

  // # Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // # Format device info for better readability
  const formatDeviceInfo = (deviceInfo) => {
    if (!deviceInfo) return 'N/A';
    
    try {
      // Extract OS information
      let os = 'Unknown OS';
      // # Check for mobile devices first
      if (/android/i.test(deviceInfo.toLowerCase())) {
        os = 'Android';
      } else if (deviceInfo.toLowerCase().includes('ipad')) {
        // # Explicitly detect iPad before checking for macOS
        os = 'iOS';
      } else if (deviceInfo.includes('Win')) {
        os = 'Windows';
      } else if (deviceInfo.includes('Mac')) {
        os = 'macOS';
      } else if (deviceInfo.includes('iOS') || deviceInfo.includes('iPhone') || deviceInfo.includes('iPad')) {
        os = 'iOS';
      } else if (deviceInfo.includes('Linux') && !/android/i.test(deviceInfo.toLowerCase())) {
        // # Only set Linux if it's not actually Android
        os = 'Linux';
      }
      
      // Extract browser information
      let browser = 'Unknown Browser';
      if (deviceInfo.includes('Firefox')) {
        browser = 'Firefox';
      } else if (deviceInfo.includes('Edge') || deviceInfo.includes('EdgA')) {
        browser = 'Edge';
      } else if (deviceInfo.includes('Chrome')) {
        browser = 'Chrome';
      } else if (deviceInfo.includes('Safari') && !deviceInfo.includes('Chrome')) {
        browser = 'Safari';
      } else if (deviceInfo.includes('Opera')) {
        browser = 'Opera';
      }
      
      return `${os} · ${browser}`;
    } catch (error) {
      console.error('Error formatting device info:', error);
      return deviceInfo;
    }
  };

  // # Get allowed fields to update
  const getAllowedFields = (updates) => {
    const allowedFields = [
      'firstName',
      'middleName',
      'lastName',
      'phoneNumber',
      'gender',
      'nationality',
      'university',
      'phase',
      'gpa',
      'status',
      'role',
      'selectedCountry'
    ];

    return Object.keys(updates)
      .filter(key => allowedFields.includes(key))
      .reduce((obj, key) => {
        obj[key] = updates[key];
        return obj;
      }, {});
  };

  // # Handle save with batch write
  const handleSaveWithBatch = async () => {
    try {
      // # Start a new batch
      const batch = writeBatch(db);

      // # Get sanitized updates
      const sanitizedUpdates = getAllowedFields(editData);

      // # Add metadata and created_at
      const updatedData = {
        ...sanitizedUpdates,
        created_at: selectedUser?.created_at || new Date().toISOString(), // # Add created_at if not exists
        metadata: {
          ...selectedUser?.metadata,
          updatedAt: new Date().toISOString()
        }
      };

      // # Add to batch - update user document
      const userRef = doc(db, 'users', selectedUser.uid);
      batch.update(userRef, updatedData);

      // # Commit the batch
      await batch.commit();

      // # Update local state
      setEditData(updatedData);
      onSave();
    } catch (error) {
      console.error('Error updating user:', error);
      throw new Error(error.message || 'Error updating user data');
    }
  };

  // # Check if profile is complete
  const isProfileComplete = (user) => {
    if (!user) return false;
    
    const requiredFields = [
      'firstName',
      'lastName',
      'email',
      'phoneNumber',
      'gender',
      'nationality',
      'university',
      'phase',
      'gpa'
    ];
    
    return requiredFields.every(field => user[field]);
  };

  // # Format gender with proper capitalization
  const formatGender = (gender) => {
    if (!gender) return 'N/A';
    return gender.charAt(0).toUpperCase() + gender.slice(1);
  };

  // # Get nationality data with flag
  const getNationalityData = (nationalityCode) => {
    if (!nationalityCode) return { flag: '', name: 'N/A' };
    return nationalityData[nationalityCode] || { flag: '', name: nationalityCode };
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="usr_edt_dialog"
    >
      <DialogTitle className="usr_edt_dialog-title">
        {isEditing ? 'Edit User' : 'User Details'}
      </DialogTitle>
      
      <DialogContent className="usr_edt_dialog-content">
        <div className="usr_edt_user-header">
          <img 
            src={selectedUser?.photoURL || '/icons/pfp.png'} 
            alt={formatName(selectedUser)}
            className="usr_edt_user-avatar"
          />
          <h2>{formatName(selectedUser)}</h2>
        </div>

        <div className="usr_edt_sections-grid">
          {/* # Personal Information Section */}
          <div className="usr_edt_section">
            <h3>Personal Information</h3>
            {isEditing ? (
              <>
                <div className="usr_edt_input-group">
                  <label>First Name</label>
                  <input
                    name="firstName"
                    value={editData.firstName}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="usr_edt_input-group">
                  <label>Middle Name</label>
                  <input
                    name="middleName"
                    value={editData.middleName}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="usr_edt_input-group">
                  <label>Last Name</label>
                  <input
                    name="lastName"
                    value={editData.lastName}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="usr_edt_input-group">
                  <label>Phone</label>
                  <div className="usr_edt_phone-input">
                    <Select
                      value={editData.selectedCountry?.code || '+20'}
                      onChange={(e) => {
                        const country = countryCodes.find(c => c.code === e.target.value);
                        handleSelectChange('selectedCountry', country);
                      }}
                      className="usr_edt_phone-code-select"
                    >
                      {countryCodes.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {country.flag} {country.code}
                        </MenuItem>
                      ))}
                    </Select>
                    <input
                      name="phoneNumber"
                      value={editData.phoneNumber}
                      onChange={handleInputChange}
                      className="usr_edt_phone-number"
                    />
                  </div>
                </div>
                <div className="usr_edt_input-group">
                  <FormControl fullWidth>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      name="gender"
                      value={editData.gender}
                      onChange={handleInputChange}
                      label="Gender"
                    >
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <div className="usr_edt_input-group">
                  <FormControl fullWidth>
                    <InputLabel>Nationality</InputLabel>
                    <Select
                      name="nationality"
                      value={editData.nationality}
                      onChange={handleInputChange}
                      label="Nationality"
                    >
                      {Object.entries(nationalityData).map(([key, data]) => (
                        <MenuItem key={key} value={key}>
                          {data.flag} {data.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </>
            ) : (
              <>
                <p className="usr_edt_info-row"><MdEmail className="usr_edt_info-icon" /> <strong>Email:</strong> {selectedUser?.email}</p>
                <p className="usr_edt_info-row">
                  <MdPhone className="usr_edt_info-icon" /> 
                  <strong>Phone:</strong> 
                  <span className="usr_edt_phone-display">
                    {selectedUser?.selectedCountry?.code && (
                      <>
                        <span className="usr_edt_flag-icon">
                          {countryCodes.find(c => c.code === selectedUser.selectedCountry.code)?.flag || ''}
                        </span>
                        {' | '}
                      </>
                    )}
                    {selectedUser?.selectedCountry?.code || ''} {selectedUser?.phoneNumber || 'N/A'}
                  </span>
                </p>
                <p className="usr_edt_info-row">
                  <MdWc className="usr_edt_info-icon" /> 
                  <strong>Gender:</strong> 
                  {selectedUser?.gender ? (
                    <span className={`usr_edt_gender-badge ${selectedUser?.gender}`}>
                      {formatGender(selectedUser?.gender)}
                    </span>
                  ) : 'N/A'}
                </p>
                <p className="usr_edt_info-row">
                  <MdPublic className="usr_edt_info-icon" /> 
                  <strong>Nationality:</strong> 
                  <span className="usr_edt_nationality-display">
                    {getNationalityData(selectedUser?.nationality).flag && (
                      <span className="usr_edt_flag-icon">{getNationalityData(selectedUser?.nationality).flag}</span>
                    )} {selectedUser?.nationality || 'N/A'}
                  </span>
                </p>
              </>
            )}
          </div>

          {/* # Academic Information Section */}
          <div className="usr_edt_section">
            <h3>Academic Information</h3>
            {isEditing ? (
              <>
                <div className="usr_edt_input-group">
                  <label>University</label>
                  <input
                    name="university"
                    value={editData.university}
                    onChange={handleInputChange}
                    placeholder="Enter university"
                  />
                </div>
                <div className="usr_edt_input-group">
                  <FormControl fullWidth>
                    <InputLabel>Phase</InputLabel>
                    <Select
                      name="phase"
                      value={editData.phase}
                      onChange={handleInputChange}
                      label="Phase"
                    >
                      <MenuItem value="basic-science">Academic Phase (Basic Science)</MenuItem>
                      <MenuItem value="clinical-medicine">Clinical Phase (Clinical Medicine)</MenuItem>
                      <MenuItem value="intern">Intern</MenuItem>
                      <MenuItem value="new-graduate">New Graduate (&lt; 3 years)</MenuItem>
                      <MenuItem value="old-graduate">Old Graduate (&gt; 3 years)</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <div className="usr_edt_input-group">
                  <label>GPA</label>
                  <input
                    name="gpa"
                    value={editData.gpa}
                    onChange={handleInputChange}
                    placeholder="Enter GPA"
                    type="number"
                    step="0.01"
                    min="0"
                    max="4"
                  />
                </div>
              </>
            ) : (
              <>
                <p className="usr_edt_info-row"><MdSchool className="usr_edt_info-icon" /> <strong>University:</strong> {selectedUser?.university || 'N/A'}</p>
                <p className="usr_edt_info-row"><MdTimeline className="usr_edt_info-icon" /> <strong>Phase:</strong> {selectedUser?.phase || 'N/A'}</p>
                <p className="usr_edt_info-row"><MdGrade className="usr_edt_info-icon" /> <strong>GPA:</strong> {selectedUser?.gpa || 'N/A'}</p>
              </>
            )}
          </div>

          {/* # Account Information Section */}
          <div className="usr_edt_section">
            <h3>Account Information</h3>
            {isEditing ? (
              <>
                <div className="usr_edt_input-group">
                  <FormControl fullWidth>
                    <InputLabel>Role</InputLabel>
                    <Select
                      name="role"
                      value={editData.role}
                      onChange={handleInputChange}
                      label="Role"
                    >
                      <MenuItem value="user">User</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="mentor">Mentor</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <div className="usr_edt_input-group">
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={editData.status}
                      onChange={handleInputChange}
                      label="Status"
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                      <MenuItem value="suspended">Suspended</MenuItem>
                    </Select>
                  </FormControl>
                </div>
              </>
            ) : (
              <>
                <p className="usr_edt_info-row"><MdVpnKey className="usr_edt_info-icon" /> <strong>UID:</strong> {selectedUser?.uid}</p>
                <p className="usr_edt_info-row"><MdAdminPanelSettings className="usr_edt_info-icon" /> <strong>Role:</strong> {selectedUser?.role}</p>
                <p className="usr_edt_info-row"><MdPerson className="usr_edt_info-icon" /> <strong>Status:</strong> {selectedUser?.status}</p>
                <p className="usr_edt_info-row"><MdEmail className="usr_edt_info-icon" /> <strong>Email Verified:</strong> {selectedUser?.emailVerified ? 'Yes' : 'No'}</p>
                <p className="usr_edt_info-row">
                  {isProfileComplete(selectedUser) ? (
                    <MdVerified className="usr_edt_info-icon usr_edt_icon-complete" />
                  ) : (
                    <MdWarning className="usr_edt_info-icon usr_edt_icon-incomplete" />
                  )}
                  <strong>Profile:</strong> 
                  <span className={`usr_edt_profile-status ${isProfileComplete(selectedUser) ? 'complete' : 'incomplete'}`}>
                    {isProfileComplete(selectedUser) ? 'Complete' : 'Incomplete'}
                  </span>
                </p>
              </>
            )}
          </div>

          {/* # Metadata Section */}
          <div className="usr_edt_section">
            <h3>Activity Information</h3>
            <p className="usr_edt_info-row"><MdAccessTime className="usr_edt_info-icon" /> <strong>Created At:</strong> {formatDate(selectedUser?.created_at)}</p>
            <p className="usr_edt_info-row"><MdUpdate className="usr_edt_info-icon" /> <strong>Last Updated:</strong> {formatDate(selectedUser?.metadata?.updatedAt)}</p>
            <p className="usr_edt_info-row"><MdLogin className="usr_edt_info-icon" /> <strong>Last Sign In:</strong> {formatDate(lastSignIn?.timestamp)}</p>
            {lastSignIn && (
              <>
                <p className="usr_edt_info-row"><MdDevices className="usr_edt_info-icon" /> <strong>Last Device:</strong> {formatDeviceInfo(lastSignIn.device)}</p>
                <p className="usr_edt_info-row"><MdLanguage className="usr_edt_info-icon" /> <strong>Last IP:</strong> {lastSignIn.ip_address || 'N/A'}</p>
                <p className="usr_edt_info-row"><MdLocationOn className="usr_edt_info-icon" /> <strong>Last Timezone:</strong> {lastSignIn.timezone || 'N/A'}</p>
              </>
            )}
          </div>
        </div>

        {updateError && (
          <div className="usr_edt_error-message">
            {updateError}
          </div>
        )}
      </DialogContent>

      <DialogActions className="usr_edt_dialog-actions">
        {!isEditing ? (
          <>
            <Button 
              onClick={() => setIsEditing(true)}
              variant="contained" 
              color="primary"
            >
              Edit User
            </Button>
            <Button
              onClick={() => onDelete(selectedUser)}
              variant="contained"
              color="error"
              disabled={isDeleting}
            >
              Delete User
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={handleSaveWithBatch}
              variant="contained" 
              color="success"
              disabled={isDeleting}
            >
              Save Changes
            </Button>
            <Button 
              onClick={() => setIsEditing(false)}
              variant="contained"
              color="inherit"
            >
              Cancel
            </Button>
          </>
        )}
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserEdit;
